import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useReducedMotion from '@/hooks/useReducedMotion';

const SLIDES = [
  {
    id: 2,
    image: 'https://media.base44.com/images/public/69f6f4084329a31d2c3702bc/7d4c9c264_02c89f2e9_hero-voiture1.webp',
    imageMobile: 'https://media.base44.com/images/public/69f6f4084329a31d2c3702bc/dc16ba62f_d3797f785_hero-voiture-800.webp',
    w: 1408,
    h: 768,
    badge: '🏎️ VOITURES PREMIUM',
    title: 'COLLECTION\nSCALEXTRIC 2026',
    subtitle: 'Des modèles 1:32 d\'une précision\net d\'un réalisme incroyables',
    cta: 'VOIR LES VOITURES',
    link: '/shop?cat=voitures',
  },
  {
    id: 3,
    image: 'https://media.base44.com/images/public/69f6f4084329a31d2c3702bc/944f79cb3_coffretcircuit1600.jpg',
    imageMobile: 'https://base44.app/api/apps/69f6f4084329a31d2c3702bc/files/mp/public/69f6f4084329a31d2c3702bc/1a37cf131_coffretcircuit-800.webp',
    w: 1600,
    h: 870,
    badge: '🎁 COFFRETS CIRCUITS',
    title: 'OFFREZ\nL\'EXPÉRIENCE ULTIME',
    subtitle: 'Coffrets complets pour débutants\net passionnés, prêts à rouler !',
    cta: 'VOIR LES COFFRETS',
    link: '/shop?cat=coffrets-circuits',
  },
];

export default function CSHeroSlider() {
  const prefersReducedMotion = useReducedMotion();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (idx) => setCurrent(idx);
  const prev = () => setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length);
  const next = () => setCurrent(c => (c + 1) % SLIDES.length);

  return (
    <div className="relative w-full h-[420px] md:h-[500px] overflow-hidden bg-gray-900">
      <div
        className="flex h-full"
        style={{
          transform: `translateX(-${current * 100}%)`,
          transition: prefersReducedMotion ? 'none' : 'transform 0.45s ease-out',
        }}
      >
        {SLIDES.map((slide, i) => (
          <div key={slide.id} className="relative w-full h-full flex-none">
            <picture>
              <source media="(max-width: 768px)" srcSet={slide.imageMobile} type="image/webp" />
              <source media="(min-width: 769px)" srcSet={slide.image} type="image/webp" />
              <img
                src={slide.image}
                alt=""
                width={slide.w}
                height={slide.h}
                fetchpriority={i === 0 ? 'high' : 'auto'}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-full object-cover"
              />
            </picture>
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-screen-xl mx-auto px-6 md:px-12 w-full">
                <span className="inline-block bg-cs-blue text-white text-xs font-bold px-3 py-1 rounded mb-4">
                  {slide.badge}
                </span>
                <h1 className="font-display font-bold text-4xl md:text-6xl text-white leading-tight mb-4 whitespace-pre-line drop-shadow">
                  {slide.title}
                </h1>
                <p className="text-white/80 text-base mb-6 whitespace-pre-line drop-shadow">
                  {slide.subtitle}
                </p>
                <Link to={slide.link}>
                  <button className="bg-cs-blue text-white font-display font-bold px-7 py-3 rounded hover:bg-cs-blue-dark transition-colors text-base">
                    {slide.cta}
                  </button>
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Arrows */}
      <button onClick={prev} aria-label="Diapositive précédente" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button onClick={next} aria-label="Diapositive suivante" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-colors backdrop-blur-sm">
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Aller à la diapositive ${i + 1}`}
            className="flex items-center justify-center w-11 h-11 p-0 m-0 bg-transparent border-0 cursor-pointer"
          >
            <span
              className={`block rounded-full transition-all duration-300 h-2 ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}